import * as Sentry from '@sentry/react';
import { User } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../../design-system/button/button';
import { DataTable } from '../../../../design-system/data-table/data-table';
import { Inline } from '../../../../design-system/inline/inline';
import { EmployeeView, PayrollRecordView } from '../../../../types/entity-types';
import { PayrollRecordInput } from '../../hooks/use-payroll-record';
import { employeesTableColumnDefinition } from './employees-table-column-definition';

type EmployeesTableProps = {
  employees: EmployeeView[];
  showHeader?: boolean;
  onEditEmployee?: (employeeId: string) => void;
  onAddEmployee?: () => void;
  onContinue?: () => void;
  selectedRowIds: string[];
  setSelectedRowIds: (ids: string[]) => void;
  payrollId?: string;
  addPayrollRecords?: (payrollId: string, records: PayrollRecordInput[]) => Promise<PayrollRecordView[]>;
  isLoading?: boolean;
};

export const EmployeesTable = ({
  employees,
  onEditEmployee,
  onAddEmployee,
  onContinue,
  selectedRowIds,
  setSelectedRowIds,
  payrollId,
  addPayrollRecords,
  isLoading = false,
}: EmployeesTableProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tableActionButtonConfig = {
    primary: {
      isVisible: !!onAddEmployee,
      text: 'Add Employee',
      onClick: onAddEmployee || (() => {}),
      icon: <User size={16} />,
    },
    secondary: {
      isVisible: false,
      text: '',
      onClick: () => {},
    },
  };

  const handleContinue = async () => {
    if (!payrollId || !addPayrollRecords) {
      onContinue?.();
      return;
    }

    setIsSubmitting(true);
    try {
      const records = selectedRowIds.map((employeeId) => ({
        employeeId,
      }));

      await addPayrollRecords(payrollId, records);
      onContinue?.();
    } catch (error) {
      toast.error('Failed to add employees to payroll');
      Sentry.captureException(error, {
        extra: {
          payrollId,
          employeesCount: selectedRowIds.length,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Inline space={2} className="mt-10 w-full" wrap="nowrap">
        <h2 className="text-xl font-semibold">Employees</h2>
        <p className="text-sm text-seapoint-gray-text">({employees.length})</p>
      </Inline>

      <DataTable<EmployeeView>
        columns={employeesTableColumnDefinition}
        data={employees}
        tableActionButtonConfig={tableActionButtonConfig}
        className="flex-1"
        meta={{
          rowHeight: 76,
          onEditEmployee,
          selectedRowIds,
          setSelectedRowIds,
        }}
      />

      {onContinue && (
        <div className="flex justify-end">
          <Button
            onClick={handleContinue}
            disabled={selectedRowIds.length === 0 || isSubmitting || isLoading}
            isLoading={isSubmitting || isLoading}
          >
            Continue
          </Button>
        </div>
      )}
    </div>
  );
};
